/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 */

import React from 'react';
import { Icon } from 'semantic-ui-react';
import TimeAgo from 'react-timeago';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

const statusStyles = {
  COMPLETED: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  TAINTED: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  STOPPED: { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
  PENDING: { bg: '#fefce8', color: '#ca8a04', border: '#fde68a' },
  STARTING: { bg: '#fefce8', color: '#ca8a04', border: '#fde68a' },
  STOPPING: { bg: '#fefce8', color: '#ca8a04', border: '#fde68a' },
  RESTARTING: { bg: '#fefce8', color: '#ca8a04', border: '#fde68a' },
  TERMINATING: { bg: '#fefce8', color: '#ca8a04', border: '#fde68a' },
  FAILED: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  STARTING_FAILED: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  STOPPING_FAILED: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  TERMINATING_FAILED: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  TERMINATED: { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' },
  EXPIRED: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  COMPLETED_EXPIRED: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  INSUFFICIENT_FUNDS: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
};

function StatusBadge({ status }) {
  const s = statusStyles[status] || { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };
  return (
    <span
      className="workspace-status-badge"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontSize: '0.7em' }}
    >
      {status}
    </span>
  );
}

// expected props
// - columns, as array of { key, label, sortable, type }
// - rows, as map of { [column]: value }
// - sorts, as array of { field, desc }
// - onSort(key, event)
export default function CompactTable({ columns, rows, sorts = [], onSort }) {
  function handleSort(key, e) {
    if (onSort) onSort(key, e);
  }

  function renderSortIcon(key) {
    const entry = sorts.find(s => s.field === key);
    if (!entry) return null;
    const idx = sorts.length > 1 ? sorts.indexOf(entry) : -1;
    return (
      <>
        <Icon
          name={entry.desc ? 'caret down' : 'caret up'}
          style={{ ...iconStyle, marginLeft: '4px', fontSize: '0.8em' }}
        />
        {idx >= 0 && (
          <span style={{ fontSize: '0.65em', color: '#9ca3af', marginLeft: '2px' }}>{idx + 1}</span>
        )}
      </>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="workspace-empty-state" style={{ marginTop: '24px' }}>
        <div className="workspace-empty-state-icon"><Icon name="filter" /></div>
        <div className="workspace-empty-state-title">No workspaces match filters</div>
        <div className="workspace-empty-state-subtitle">Try adjusting your filter criteria.</div>
      </div>
    );
  }

  return (
    <>
      <div style={{ fontStyle: 'italic', color: '#9ca3af', textAlign: 'right', fontSize: '0.8em', marginBottom: '6px', marginTop: '8px' }}>
        Hold Ctrl and click column headers to sort by multiple columns.
      </div>
      <table className="config-table">
        <thead>
          <tr>
            {columns.map(({ key, label, sortable = true }) => (
              <th
                key={`header-${key}`}
                style={sortable ? { cursor: 'pointer', userSelect: 'none' } : undefined}
                onClick={sortable ? (e) => handleSort(key, e) : undefined}
              >
                {label}
                {sortable && renderSortIcon(key)}
              </th>
            ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.id}>
              {columns.map(({ key, type = 'string' }) => (
                <td key={`${row.id}-${key}`} style={{ fontSize: '0.85em' }}>
                  {key === 'status' ? (
                    <StatusBadge status={row[key]} />
                  ) : type === 'date' ? (
                    <span style={{ color: '#6b7280' }}><TimeAgo date={row[key]} /></span>
                  ) : key === 'name' ? (
                    <span className="config-name">{row[key]}</span>
                  ) : (
                    <span style={{ color: '#374151' }}>{row[key]}</span>
                  )}
                </td>
              ))}
              <td>{row.actions}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
